import { NonNativeTextTracksData } from 'hls.js';
import {
  ErrorCode,
  reportError,
} from 'services/analytics/reportingServicePage';
import { getCommonLanguageCode } from 'support/playerUtils';
import { translate } from 'support/translate';

export default class NonNativeTextTrackHandler {
  private videoElement: HTMLMediaElement;

  private nonNativeTextTracks: TextTrack[] | undefined;
  private nonNativeTextTrackCueIds: Set<string> | undefined;
  private selectedNonNativeTextTrack: TextTrack | undefined;

  constructor(videoElement: HTMLMediaElement) {
    this.videoElement = videoElement;
  }

  get nonNativeTextTracksAvailable(): boolean {
    return !!this.nonNativeTextTracks?.length;
  }

  get nonNativeTextTrackLanguages(): string[] {
    return this.nonNativeTextTracks?.map(track => track.language) ?? [];
  }
  get nonNativeTextTrackIds(): string[] {
    return this.nonNativeTextTracks?.map(track => track.label) ?? [];
  }

  addHlsTextTracks(data: NonNativeTextTracksData): void {
    this.nonNativeTextTracks = data.tracks
      .map(track => {
        const { _id, kind, closedCaptions, subtitleTrack } = track;
        const language =
          closedCaptions?.lang ||
          subtitleTrack?.lang ||
          translate('closedCaption.english'); // default to English if not labelled

        return this.videoElement.addTextTrack(
          kind as TextTrackKind,
          _id, // Use ID as label for cue logic
          getCommonLanguageCode(language),
        );
      })
      .filter(textTrack => !!textTrack) as TextTrack[];
  }

  addTextTracks(tracks: TextTrack[]) {
    this.nonNativeTextTracks = tracks.map(track => {
      const { kind, id, language } = track;
      return this.videoElement.addTextTrack(
        kind,
        id, // Use ID as label for cue logic
        getCommonLanguageCode(language),
      );
    });
  }

  /**
   * Enable non-native text track for a specific language if that language exists
   * @param lang The text track language we want to enable
   * @returns True if text track was enabled, false otherwise
   */
  enableNonNativeTextTrack(lang: string): boolean {
    this.disableNonNativeTextTrack();

    const selectedLanguage = getCommonLanguageCode(lang);
    const textTrack = this.nonNativeTextTracks?.find(textTrack => {
      const textTrackLanguage = getCommonLanguageCode(textTrack.language);
      return selectedLanguage === textTrackLanguage;
    });
    if (!textTrack) return false;

    textTrack.mode = 'showing';
    this.selectedNonNativeTextTrack = textTrack;

    this.nonNativeTextTrackCueIds = new Set<string>();
    for (const cue of textTrack.cues ?? []) {
      this.nonNativeTextTrackCueIds.add(cue.id);
    }

    textTrack.oncuechange = () => {
      for (const cue of textTrack.activeCues ?? []) {
        const { id } = cue as VTTCue;
        this.nonNativeTextTrackCueIds?.delete(id);
      }
    };
    return true;
  }

  disableNonNativeTextTrack(): void {
    if (this.selectedNonNativeTextTrack) {
      this.selectedNonNativeTextTrack.mode = 'hidden';
      this.selectedNonNativeTextTrack.oncuechange = null;
    }

    this.selectedNonNativeTextTrack = undefined;
    this.nonNativeTextTrackCueIds = undefined;
  }

  addNonNativeTextTrackCues(
    cues: VTTCue[],
    trackId: string | null = null,
  ): void {
    try {
      const selectedTrack = this.selectedNonNativeTextTrack;
      const isCorrectId = trackId === null || selectedTrack?.label === trackId;
      if (trackId === null && !selectedTrack) return; // if we don't pass a trackId we check that we have a selected track
      if (!isCorrectId) return;

      cues.forEach(cue => {
        const { id } = cue;
        // Make sure we don't add duplicate cues
        if (this.nonNativeTextTrackCueIds?.has(id)) return;

        selectedTrack!.addCue(cue);
        this.nonNativeTextTrackCueIds!.add(id);
      });
    } catch (e) {
      // Unable to add cue, ignore
      reportError({
        error: e,
        userFacing: false,
        code: ErrorCode.c35,
      });
    }
  }

  clearNonNativeTextTracks(): void {
    this.disableNonNativeTextTrack();
    this.nonNativeTextTracks = undefined;
  }

  getSelectedTrackId(): string | undefined {
    return this.selectedNonNativeTextTrack?.label; // we can't set id so we pass label instead
  }
}
